// src/components/mdx/client/visualizations/MovingAveragePurposeChart.client.tsx

/**
 * Moving Average Purpose Chart
 *
 * This visualization demonstrates how smoothing noisy datasets with a moving average
 * reveals the underlying trend. The component centralizes all chart imports through
 * `~/lib/charts/client` so every MDX visualization shares the same Recharts runtime
 * chunk, and it sources deterministic sample data from the shared movingAverageData
 * helpers to avoid expensive random allocations on every render.
 *
 * OPTIMIZATION NOTE:
 * Re-introduced documentation after refactor to clarify that we rely on shared
 * chart imports for bundle deduplication and deterministic data generation to
 * eliminate layout thrash caused by non-memoized data.
 */

'use client'

import { forwardRef, useCallback, useMemo } from 'react'

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from '~/lib/charts/client'
import { getOrCreateTooltipWrapper } from '~/lib/charts/tooltip'
import type { CustomTooltipProps } from '~/lib/charts/tooltip'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { AXIS_TOKENS, MOVING_AVERAGE_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import {
  calculateMovingAverageSeries,
  generateMovingAverageData,
  type MovingAverageDataPoint
} from '~/components/mdx/client/visualizations/movingAverageData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'
import { createDeterministicRandom, hashSeed } from '~/lib/utils/random'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'

/**
 * Props for the ChartContainer component
 * @interface ChartContainerProps
 * @extends React.HTMLAttributes<HTMLDivElement>
 * @property {Object} [config] - Configuration for chart series
 *   @property {string} config[key].label - Display label for the series
 *   @property {string} config[key].color - Color value for the series
 */
interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: Record<string, { label: string; color: string }>
}

/**
 * A container component for charts with forwarded refs
 * 
 * @param {ChartContainerProps} props - Component props
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref to the container div
 * @returns {JSX.Element} The rendered chart container
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(({ className, ...props }, ref) => {
  return (
    <div className={className} {...props} ref={ref}>
      {props.children}
    </div>
  )
})
ChartContainer.displayName = 'ChartContainer'

/**
 * Type alias for data points used in the moving average chart
 * @typedef {MovingAverageDataPoint} DataPoint
 */
type DataPoint = MovingAverageDataPoint

/**
 * Custom tooltip component for the moving average chart
 * 
 * @param {CustomTooltipProps} props - Tooltip props from Recharts
 *   @param {boolean} props.active - Whether the tooltip is active
 *   @param {Array} props.payload - Data payload for the tooltip
 *   @param {string | number} props.label - Label for the current data point
 * @returns {JSX.Element | null} The rendered tooltip or null if inactive
 */
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps): JSX.Element | null => {
  if (!active || !payload?.length || !payload[0]?.payload) {
    return null
  }

  const dataPoint = payload[0].payload as unknown as DataPoint
  const isHighlight = dataPoint.isHighlight

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/90 p-4 shadow-lg backdrop-blur-sm">
      <p className="mb-2 font-medium text-gray-300">Day: {label}</p>
      <p className="text-sm text-gray-400">
        Raw Value: <span className="font-medium text-blue-400">{payload[0].value.toFixed(2)}</span>
        {isHighlight && dataPoint.highlightType === 'high' && (
          <span className="ml-2 text-xs font-bold text-green-400">(Local Peak)</span>
        )}
        {isHighlight && dataPoint.highlightType === 'low' && (
          <span className="ml-2 text-xs font-bold text-red-400">(Local Bottom)</span>
        )}
      </p>
      <p className="text-sm text-gray-400">
        Moving Avg: <span className="font-medium text-orange-400">{payload[1]?.value.toFixed(2)}</span>
      </p>
    </div>
  )
}

/**
 * Props for the custom dot renderer
 * @interface DotProps
 * @property {number} cx - X coordinate of the dot center
 * @property {number} cy - Y coordinate of the dot center
 * @property {number} index - Index of the data point
 * @property {DataPoint} payload - The data point being rendered
 */
interface DotProps {
  cx: number
  cy: number
  index: number
  payload: DataPoint
}

/**
 * Props for the MovingAveragePurposeChartContent component
 * @interface MovingAveragePurposeChartContentProps
 * @property {boolean} isActive - Indicates whether the visualization is currently visible to the reader
 */
interface MovingAveragePurposeChartContentProps {
  isActive: boolean
}

/**
 * The main chart content component that renders the moving average visualization
 * 
 * @param {MovingAveragePurposeChartContentProps} props - Component props
 *   @param {boolean} props.isActive - Whether the chart is currently visible
 * @returns {JSX.Element} The rendered chart content
 */
function MovingAveragePurposeChartContent({ isActive }: MovingAveragePurposeChartContentProps): JSX.Element {
  const numPoints = 100
  
  /**
   * Generate deterministic data for the chart using a seeded random number generator
   * This ensures consistent visualization across renders and enables reliable testing
   */
  const data = useMemo<DataPoint[]>(() => {
    const rng = createDeterministicRandom(hashSeed('moving-average-purpose', numPoints))
    const rawData = generateMovingAverageData(numPoints, rng)
    return calculateMovingAverageSeries(rawData, 10)
  }, [numPoints])

  /**
   * Calculate phase boundaries for the different data regimes
   * Each phase demonstrates a different characteristic of moving averages
   */
  const phaseBoundaries = useMemo(() => {
    const stablePhase = 0.2
    const trendingUpPhase = 0.25
    const falseDownPhase = 0.15
    const volatilePhase = 0.2

    return {
      stablePhaseEnd: Math.floor(numPoints * stablePhase),
      trendingUpPhaseEnd: Math.floor(numPoints * (stablePhase + trendingUpPhase)),
      falseDownPhaseEnd: Math.floor(numPoints * (stablePhase + trendingUpPhase + falseDownPhase)),
      volatilePhaseEnd: Math.floor(numPoints * (stablePhase + trendingUpPhase + falseDownPhase + volatilePhase)),
      trendingDownPhaseEnd: numPoints - 1
    }
  }, [numPoints])

  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const tooltipContent = useMemo(() => getOrCreateTooltipWrapper(CustomTooltip), [])
  
  const margin = useMemo(
    () =>
      isMobile
        ? { top: 10, right: 10, left: 0, bottom: 10 }
        : { top: 20, right: 15, left: 10, bottom: 20 },
    [isMobile]
  )

  const colorPalette = useMemo(
    () => ({
      grid: AXIS_TOKENS.grid,
      text: AXIS_TOKENS.stroke,
      rawData: MOVING_AVERAGE_SERIES.informational,
      movingAvg: MOVING_AVERAGE_SERIES.conversion
    }),
    []
  )

  // Only animate when the chart is visible and user hasn't requested reduced motion
  const animateSeries = isActive && !prefersReducedMotion

  /**
   * Custom dot renderer to highlight peaks and troughs in the data
   * 
   * @param {DotProps} props - Dot rendering props
   *   @param {number} props.cx - X coordinate
   *   @param {number} props.cy - Y coordinate
   *   @param {DataPoint} props.payload - Data point being rendered
   *   @param {number} props.index - Index of the data point
   * @returns {JSX.Element} The rendered dot element
   */
  const renderDot = useCallback(
    ({ cx, cy, payload, index }: DotProps): JSX.Element => {
      if (payload.isHighlight) {
        const color = payload.highlightType === 'high' ? 'rgb(74, 222, 128)' : 'rgb(248, 113, 113)'
        const size = isMobile ? 6 : 8

        return (
          <svg
            key={`dot-${index}-${payload.day}-${payload.highlightType}`}
            x={cx - size / 2}
            y={cy - size / 2}
            width={size}
            height={size}
          >
            <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} />
          </svg>
        )
      }

      // Return empty SVG for non-highlighted points
      return <svg key={`dot-${index}-${payload.day}`} width={0} height={0} />
    },
    [isMobile]
  )

  return (
    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl sm:rounded-3xl">
      <div className="p-4 sm:p-8">
        <h2 className="mb-1 text-xl font-bold text-transparent sm:mb-2 sm:text-3xl bg-clip-text bg-gradient-to-r from-blue-400 to-orange-400">
          The Power of Moving Averages
        </h2>
        <p className="mb-3 text-sm text-gray-400 sm:mb-6 sm:text-base">Revealing true trends in noisy data</p>
        <ChartContainer
          config={{
            value: { label: 'Raw Data', color: 'rgb(96, 165, 250)' },
            movingAverage: { label: '10-Day Moving Average', color: 'rgb(251, 146, 60)' }
          }}
          className="aspect-[4/3] w-full sm:aspect-[16/9]"
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
            debounce={300}
            initialDimension={{ width: 800, height: 400 }}
          >
            <LineChart
              data={data}
              margin={margin}
              accessibilityLayer={false}
              throttleDelay={50}
              syncId="movingAverageChart"
            >
              <CartesianGrid opacity={0.2} stroke={colorPalette.grid} strokeDasharray="3 3" />
              <XAxis
                dataKey="day"
                stroke={colorPalette.text}
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                interval={isMobile ? 19 : 9}
                label={
                  isMobile
                    ? undefined
                    : {
                        value: 'Time (Days)',
                        position: 'insideBottom',
                        offset: -10,
                        fill: colorPalette.text
                      }
                }
              />
              <YAxis
                stroke={colorPalette.text}
                fontSize={isMobile ? 10 : 12}
                tickLine={false}
                axisLine={false}
                domain={[0, 140]}
                width={isMobile ? 25 : 40}
                label={
                  isMobile
                    ? undefined
                    : {
                        value: 'Value',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10,
                        fill: colorPalette.text
                      }
                }
              />
              <Tooltip
                content={tooltipContent as TooltipContentType<ValueType, NameType>}
                animationDuration={100}
                animationEasing="ease-out"
                shared={true}
                allowEscapeViewBox={{ x: false, y: false }}
                filterNull={false}
                includeHidden={false}
              />
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={{
                  paddingTop: isMobile ? '5px' : '0px',
                  fontSize: isMobile ? '10px' : '12px',
                  lineHeight: isMobile ? '1.2' : '1.5'
                }}
              />
              <Line
                key="raw-data-line"
                type="monotone"
                dataKey="value"
                stroke={colorPalette.rawData}
                strokeWidth={isMobile ? 0.8 : 1}
                dot={props => renderDot(props as unknown as DotProps)}
                name="Raw Data"
                animationDuration={animateSeries ? 800 : 0}
                animationEasing="ease-out"
                connectNulls={false}
              />
              <Line
                key="moving-average-line"
                type="monotone"
                dataKey="movingAverage"
                stroke={colorPalette.movingAvg}
                strokeWidth={isMobile ? 2 : 3}
                dot={false}
                name="10-Day Moving Average"
                animationDuration={animateSeries ? 800 : 0}
                animationEasing="ease-out"
                connectNulls={false}
              />
              <ReferenceArea
                key="reference-area-stable"
                x1={0}
                x2={phaseBoundaries.stablePhaseEnd}
                y1={0}
                y2={140}
                fill="rgba(96, 165, 250, 0.1)"
                strokeOpacity={0}
              />
              <ReferenceArea
                key="reference-area-trending-up"
                x1={phaseBoundaries.stablePhaseEnd}
                x2={phaseBoundaries.trendingUpPhaseEnd}
                y1={0}
                y2={140}
                fill="rgba(52, 211, 153, 0.1)"
                strokeOpacity={0}
              />
              <ReferenceArea
                key="reference-area-false-down"
                x1={phaseBoundaries.trendingUpPhaseEnd}
                x2={phaseBoundaries.falseDownPhaseEnd}
                y1={0}
                y2={140}
                fill="rgba(251, 146, 60, 0.1)"
                strokeOpacity={0}
              />
              <ReferenceArea
                key="reference-area-volatile"
                x1={phaseBoundaries.falseDownPhaseEnd}
                x2={phaseBoundaries.volatilePhaseEnd}
                y1={0}
                y2={140}
                fill="rgba(232, 121, 249, 0.1)"
                strokeOpacity={0}
              />
              <ReferenceArea
                key="reference-area-trending-down"
                x1={phaseBoundaries.volatilePhaseEnd}
                x2={phaseBoundaries.trendingDownPhaseEnd}
                y1={0}
                y2={140}
                fill="rgba(248, 113, 113, 0.1)"
                strokeOpacity={0}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
        <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs sm:mt-6 sm:gap-4 sm:text-sm">
          <div className="flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-blue-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Stable with Noise</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-emerald-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Uptrend with High Noise</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-orange-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">False Downtrend</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-fuchsia-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Volatile with Hidden Uptrend</span>
          </div>
          <div className="flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-red-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Clear Downtrend</span>
          </div>
          <div className="mt-1 flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-green-400 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Local Peaks</span>
          </div>
          <div className="mt-1 flex items-center">
            <div className="mr-1 h-3 w-3 rounded bg-red-400 sm:mr-2 sm:h-4 sm:w-4" />
            <span className="text-gray-400">Local Bottoms</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Moving Average Purpose Chart Component
 * 
 * Default export that defers rendering until the chart scrolls into view.
 * This optimization prevents unnecessary computation and rendering for charts
 * that are not currently visible to the user.
 * 
 * @returns {JSX.Element} The rendered chart with visibility boundary
 */
export default function MovingAveragePurposeChart(): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-4xl p-2 sm:p-6">
      <ChartVisibilityBoundary aria-label="Moving average smoothing demonstration">
        {({ hasEntered, isIntersecting }) =>
          hasEntered ? <MovingAveragePurposeChartContent isActive={isIntersecting} /> : null
        }
      </ChartVisibilityBoundary>
    </div>
  )
}
